<template>
  <main class="flex flex-col h-screen p-4 focus:outline-none">
    <div class="flex items-center justify-between w-full min-w-0">
      <h1 class="pl-1.5 capitalize text-xl font-medium leading-6 dark:text-dark-body-text text-gray-900 whitespace-nowrap">
        {{ $t("Playbooks") }}
      </h1>
    </div>

    <div class="flex w-full min-w-0 mt-5 space-x-2 rounded-md">
      <div class="relative flex items-stretch flex-grow w-full mx-auto ml-2 focus-within:z-10">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search class="z-10 w-5 h-5 text-gray-400" aria-hidden="true" />
        </div>
        <TextInput
          v-model="search_term"
          :value="search_term"
          input-classes="appearance-none block relative w-full pl-3"
          name="search_term"
          type="text"
          :class="[{ 'mr-10': $store.state.auth.user.user_type == 'end_user' }]"
          :validate-on-input="true"
          :label="$t('Search Playbooks')"
          :icon="true" />
      </div>
      <div v-if="$store.state.auth.user.user_type !== 'end_user'" class="pr-5">
        <button class="h-10 btn primary-btn w-36" @click="$emitter.emit(ALL_EMITTERS.CREATE_NEW_PLAYBOOK, { playbooks })">
          <Plus class="w-6 h-6" />
          <span class="ml-2">{{ $t("Create New") }}</span>
        </button>
      </div>
    </div>

    <div class="pb-32 pr-4 overflow-auto overflow-x-hidden">
      <div v-if="!search_term && $store.state.data.pending_playbook" class="flex w-full my-4">
        <div class="w-1/3">
          <h1 class="text-lg mb-3 font-medium leading-6 text-gray-900 dark:text-dark-body-text">
            {{ $t("Continue creating") }}:
          </h1>
          <PlaybookCard
            :tags="playbook_tags"
            :pending-creation="true"
            :playbook="$store.state.data.pending_playbook"
            @deletePlaybook="deletePlaybook()"
            @duplicatePlaybook="duplicatePlaybook()"
            @archivePlaybook="archivePlaybook()" />
        </div>
      </div>

      <div class="flex justify-between h-16 pl-3 pt-5 pb-2">
        <div class="flex items-center">
          <span class="flex items-center justify-center bg-green-400 rounded-full w-4 h-4 mr-3" />
          <div>
            <h1 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
              {{ $t("Active Playbooks") }}
            </h1>
            <span class="text-sm text-common-grey">{{ $t("View all running Playbooks.") }}</span>
          </div>
        </div>
        <div v-show="playbooks.filter((p) => p.is_active && p.is_selected).length > 0" class="flex items-center gap-2">
          <button type="button" class="cancel-btn" @click="selectAllPlaybooks()">
            <X
              v-if="playbooks.filter((p) => p.is_active && p.is_selected).length == playbooks.filter((p) => p.is_active).length"
              class="w-4 h-4" />
            <Check v-else class="w-4 h-4" />
            <span class="flex items-center justify-center rounded-full w-4 h-4 mr-1" />{{
              playbooks.filter((p) => p.is_active && p.is_selected).length == playbooks.filter((p) => p.is_active).length
                ? $t("Unselect All")
                : $t("Select All")
            }}
          </button>
          <button type="button" class="cancel-btn" @click="archiveSelectedActivePlaybooks()">
            <span class="flex items-center justify-center bg-red-500 rounded-full w-4 h-4 mr-3" />{{ $t("Archive Selected Playbooks") }}
          </button>
          <button type="button" class="danger-btn w-fit" @click="delete_active_playbooks_modal_is_open = true">
            <Trash2 color="red" class="w-5 h-5 mr-2 transition ease-in-out dark:text-white duration-side-menu-transition-duration" />
            {{ $t("Delete Selected Playbooks") }}
          </button>
        </div>
      </div>
      <div
        v-if="
          search_term.length > 0 &&
            filtered_playbooks?.filter((x) => x.is_active).length == 0
        "
        class="flex w-full items-center text-center justify-center m-3 bg-light-box-background dark:bg-dark-box-background min-h-[200px] shadow rounded-lg text-2xl font-normal text-gray-500">
        {{ $t("There are no active Playbooks for applied filter, try again with a different query") }}
      </div>
      <div v-else-if="filtered_playbooks?.filter((x) => x.is_active).length" class="mx-3 mt-2 flex flex-wrap gap-2.5 min-w-full">
        <div
          v-for="(playbook, index) in filtered_playbooks.filter((x) => x.is_active)"
          :key="index"
          class="w-[32.5%]">
          <PlaybookCard
            v-model:is-selected="playbook.is_selected"
            :tags="playbook_tags"
            :show-checkbox="playbooks.filter((p) => p.is_active && p.is_selected).length > 0"
            :playbook="playbook"
            @deletePlaybook="deletePlaybook(playbook)"
            @duplicatePlaybook="duplicatePlaybook(playbook)"
            @archivePlaybook="archivePlaybook(playbook)" />
        </div>
      </div>
      <div
        v-else
        class="flex w-full items-center text-center justify-center m-3 bg-light-box-background dark:bg-dark-box-background min-h-[200px] shadow rounded-lg text-2xl font-normal text-gray-500">
        {{ $t("You have no active playbooks") }}
      </div>
      <div class="flex justify-between h-16 pl-3 pt-5 pb-2">
        <div class="flex items-center">
          <span class="flex items-center justify-center bg-red-500 rounded-full w-4 h-4 mr-3" />
          <div>
            <h1 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
              {{ $t("Archived Playbooks") }}
            </h1>
            <span class="text-sm text-common-grey">{{ $t("View all stopped Playbooks.") }}</span>
          </div>
        </div>
        <div v-show="filtered_playbooks.filter((p) => !p.is_active && p.is_selected).length > 0" class="flex items-center gap-2">
          <button type="button" class="cancel-btn" @click="unArchiveSelectedPlaybooks()">
            <span class="flex items-center justify-center bg-green-400 rounded-full w-4 h-4 mr-3" />{{ $t("Activate Selected Playbooks") }}
          </button>
          <button type="button" class="danger-btn w-fit" @click="delete_archived_playbooks_modal_is_open = true">
            <Trash2 color="red" class="w-5 h-5 mr-2 transition ease-in-out dark:text-white duration-side-menu-transition-duration" />
            {{ $t("Delete Selected Playbooks") }}
          </button>
        </div>
      </div>
      <div
        v-if="
          search_term.length > 0 &&
            filtered_playbooks.filter((x) => !x.is_active).length == 0
        "
        class="flex w-full items-center text-center justify-center m-3 bg-light-box-background dark:bg-dark-box-background min-h-[200px] shadow rounded-lg text-2xl font-normal text-gray-500">
        {{ $t("There are no archived Playbooks for applied filter, try again with a different query") }}
      </div>
      <div v-else-if="filtered_playbooks.filter((x) => !x.is_active).length > 0" class="mx-3 mt-2 flex flex-wrap gap-2.5 min-w-full">
        <div
          v-for="(playbook, index) in filtered_playbooks.filter((x) => !x.is_active)"
          :key="index"
          class="w-[32.5%]">
          <PlaybookCard
            v-model:is-selected="playbook.is_selected"
            :tags="playbook_tags"
            :show-checkbox="playbooks.filter((p) => !p.is_active && p.is_selected).length > 0"
            :playbook="playbook"
            @deletePlaybook="deletePlaybook(playbook)"
            @duplicatePlaybook="duplicatePlaybook(playbook)"
            @archivePlaybook="archivePlaybook(playbook)" />
        </div>
      </div>
      <div
        v-else
        class="flex w-full items-center text-center justify-center m-3 bg-light-box-background dark:bg-dark-box-background min-h-[200px] shadow rounded-lg text-2xl font-normal text-gray-500">
        {{ $t("You have no archived playbooks") }}
      </div>
    </div>
  </main>

  <!-- delete Playbook modal  -->
  <Modal v-model:is-open="delete_playbook_modal_is_open" :default-success-msg="$t('Playbook Deleted')" :use-toast="true">
    <template #form="{ close: closeModal, done: showIsDone }">
      <button
        class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
        type="button"
        @click="closeModal">
        <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
      </button>
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Delete Playbook") }}
      </h3>
      <div class="max-w-xl mt-2 text-gray-500 text-md">
        <p>{{ $t("Do you want to delete this playbook ?") }}</p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="confirmDeletePlaybook(showIsDone)">
          {{ $t("Delete Playbook") }}
        </button>
      </div>
    </template>
  </Modal>

  <!-- delete active Playbooks modal  -->
  <Modal v-model:is-open="delete_active_playbooks_modal_is_open" :default-success-msg="$t('Playbooks Deleted')">
    <template #form="{ close: closeModal, done: showIsDone }">
      <button
        class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
        type="button"
        @click="closeModal">
        <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
      </button>
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Delete") }} {{ playbooks.filter((p) => p.is_selected).length > 1 ? $t("Playbooks") : $t("Playbook") }}
      </h3>
      <div class="max-w-xl mt-2 text-gray-500 text-md">
        <p>
          {{ $t("Do you want to delete") }}
          {{ playbooks.filter((p) => p.is_selected).length > 1 ? $t("these playbooks") : $t("this playbook") }} ?
        </p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="deleteSelectedActivePlaybooks(showIsDone)">
          {{ $t("Delete") }} {{ playbooks.filter((p) => p.is_selected).length > 1 ? $t("Playbooks") : $t("Playbook") }}
        </button>
      </div>
    </template>
  </Modal>

  <!-- delete archive Playbooks modal  -->
  <Modal v-model:is-open="delete_archived_playbooks_modal_is_open" :default-success-msg="$t('Playbooks Deleted')">
    <template #form="{ close: closeModal, done: showIsDone }">
      <button
        class="inset-y-0 right-0 flex items-center justify-center float-right w-6 h-6 ml-5 rounded-full group hover:bg-gray-300"
        type="button"
        @click="closeModal">
        <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
      </button>
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Delete") }} {{ playbooks.filter((p) => p.is_selected).length > 1 ? $t("Playbooks") : $t("Playbook") }}
      </h3>
      <div class="max-w-xl mt-2 text-gray-500 text-md">
        <p>
          {{ $t("Do you want to delete") }} {{ playbooks.filter((p) => p.is_selected).length > 1 ? "these playbooks" : "this playbook" }} ?
        </p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="deleteSelectedArchivedPlaybooks(showIsDone)">
          {{ $t("Delete") }} {{ playbooks.filter((p) => p.is_selected).length > 1 ? $t("Playbooks") : $t("Playbook") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from "@/components/shared/Modal.vue"
import TextInput from "@/components/shared/TextInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Search, X, Check, Plus, Trash2 } from "lucide-vue-next"
import PlaybookCard from "./PlaybookCard.vue"

import playbookService from "@/services/playbook.service"
import { utils } from "@/store/helpers"

export default {
  components: {
    Check,
    Modal,
    PlaybookCard,
    X,
    Plus,
    Trash2,
    Search,
    TextInput
  },
  data() {
    return {
      utils,
      ALL_EMITTERS,
      playbooks: [],
      search_term: "",
      playbook_to_be_deleted: null,
      delete_playbook_modal_is_open: false,
      delete_active_playbooks_modal_is_open: false,
      delete_archived_playbooks_modal_is_open: false,
      playbook_tags: []
    }
  },
  computed: {
    filtered_playbooks() {
      if(this.search_term){
        return utils.scoreSearch(this.search_term, this.playbooks, ["name"])
      }else{
        return this.playbooks
      }
    }
  },
  activated() {
    this.register_listeners()
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Playbooks"])
    this.playbooks = []
    this.$store.dispatch("playbook/getPlaybooks").then((playbooks) => {
      this.playbooks = playbooks.sort((a, b) => new Date(b.last_edited) - new Date(a.last_edited))
    })
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  created() {
    playbookService.getTags({ page_size: 100 }).then((res) => {
      this.playbook_tags = res.data.results
    })
    this.register_listeners()
  },
  methods: {
    deleteSelectedActivePlaybooks(showIsDone) {
      let selected_playbooks = this.playbooks.filter((p) => p.is_active && p.is_selected)
      selected_playbooks.forEach((playbook, index) => {
        this.$store.dispatch("playbook/removePlaybook", playbook.id).then(() => {
          this.playbooks = this.playbooks.filter((x) => x.id != playbook.id)
          if (index == selected_playbooks.length - 1) {
            this.delete_active_playbooks_modal_is_open = false
            showIsDone()
          }
        })
      })
    },
    deleteSelectedArchivedPlaybooks(showIsDone) {
      let selected_playbooks = this.playbooks.filter((p) => !p.is_active && p.is_selected)
      selected_playbooks.forEach((playbook, index) => {
        this.$store.dispatch("playbook/removePlaybook", playbook.id).then(() => {
          this.playbooks = this.playbooks.filter((x) => x.id != playbook.id)
          if (index == selected_playbooks.length - 1) {
            this.delete_archived_playbooks_modal_is_open = false
            showIsDone()
          }
        })
      })
    },
    selectAllPlaybooks() {
      let selected_playbooks = this.playbooks.filter((p) => p.is_active)
      let all_selected = selected_playbooks.length == this.playbooks.filter((p) => p.is_active && p.is_selected).length
      selected_playbooks.forEach((playbook) => {
        playbook.is_selected = !all_selected
      })
    },

    archiveSelectedActivePlaybooks() {
      let selected_playbooks = this.playbooks.filter((p) => p.is_active && p.is_selected)
      selected_playbooks.forEach((playbook) => {
        this.$store.dispatch("playbook/updatePlaybook", { id: playbook.id, data: { is_active: false } }).then((new_playbook) => {
          let index = this.playbooks.findIndex((x) => x.id == playbook.id)
          this.playbooks.splice(index, 1, new_playbook)
        })
      })
    },
    unArchiveSelectedPlaybooks() {
      let selected_playbooks = this.playbooks.filter((p) => !p.is_active && p.is_selected)
      selected_playbooks.forEach((playbook) => {
        this.$store.dispatch("playbook/updatePlaybook", { id: playbook.id, data: { is_active: true } }).then((new_playbook) => {
          let index = this.playbooks.findIndex((x) => x.id == playbook.id)
          this.playbooks.splice(index, 1, new_playbook)
        })
      })
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.DELETE_A_PLAYBOOK, (id) => {
        let index = this.playbooks.findIndex((x) => x.id == id)
        this.playbooks.splice(index, 1)
      })
      this.$emitter.on(ALL_EMITTERS.PLAYBOOK_TO_BE_CREATED, (data) => {
        this.$router.push({ name: "NewPlaybook", query: data })
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.DELETE_A_PLAYBOOK)
      this.$emitter.off(ALL_EMITTERS.PLAYBOOK_TO_BE_CREATED)
    },
    archivePlaybook(playbook) {
      this.$store
        .dispatch("playbook/updatePlaybook", { id: playbook.id, data: { is_active: !playbook.is_active } })
        .then((new_playbook) => {
          let index = this.playbooks.findIndex((x) => x.id == playbook.id)
          this.playbooks.splice(index, 1, new_playbook)
        })
    },
    duplicatePlaybook(playbook) {
      playbookService.duplicatePlaybook(playbook.id).then((res) => {
        this.playbooks.push(res.data)
      })
    },
    deletePlaybook(playbook) {
      this.playbook_to_be_deleted = playbook
      this.delete_playbook_modal_is_open = true
    },
    confirmDeletePlaybook(showIsDone) {
      this.$store.dispatch("playbook/removePlaybook", this.playbook_to_be_deleted.id).then(() => {
        let index = this.playbooks.findIndex((x) => x.id == this.playbook_to_be_deleted.id)
        this.playbooks.splice(index, 1)
        this.delete_playbook_modal_is_open = false
        showIsDone()
      })
    }
  }
}
</script>

<style scoped>
.gold-bottom-shadow {
  border-radius: 100% !important;
  box-shadow: 0px -25px 28px -20px #ffbe1560 !important;
}
.silver-bottom-shadow {
  border-radius: 100% !important;
  box-shadow: 0px -25px 28px -20px #dce7ec !important;
}
.bronze-bottom-shadow {
  border-radius: 100% !important;
  box-shadow: 0px -25px 28px -20px #ff5c0060 !important;
}
</style>
